import {userLevelCanSeePrices} from "@Common/logic/check-show-pricing";
import {
    ComponentLink,
    Link,
    makeChain,
}                              from "@Common/react/cst2";
import {Types}                 from "@Common/types";
import {cascadeSum}            from "@Common/utils/cascade";
import {parsePhpDate}          from "@Common/utils/date/parse-php-date";

import {consumeContext}             from "@Xpresso/loaders/consume-context";
import {AGENT_LEVEL, COMPANY_LEVEL} from "@Xpresso/routes/common/logic/user-level";
import {PrintService}               from "@Xpresso/routes/customize/steps/done/print-service/print-service";
import {Receipt}                    from "@Xpresso/routes/customize/steps/done/receipt/receipt";

type Cart = Types.Apis.Payloads.Next.Item.GetHistory.Cart;

export type CartAttribute = {label: string} & ({
    key     : "agent" | "branch",
    value   : string,
} | {
    key     : "cartId" | "itemsCount" | "priceTotal",
    value   : number,
} | {
    key     : "date",
    value   : Date,
} | {
    key      : "printReceipt",
    run     ?: () => Promise<void>,
});

export type CartAttributeKey = CartAttribute['key'];

export type CartAttributeDict = {
    [Key in CartAttribute['key']]?: CartAttribute & {key: Key};
};

export const CartAttributesService = ({printService, renderChildren}:{
    renderChildren : Link<JSX.Element, {
        mapCartAsAttributes : (cart:Cart) => CartAttributeDict,
    }>,
    printService : {
        print : (content:React.ReactNode) => void,
    },
}) => (
    makeChain<JSX.Element>()
    .then(...consumeContext("company"))
    .then(...consumeContext("userLevels"))
    .then(({company, userLevels: {userLevel}}) => renderChildren({
        mapCartAsAttributes : (cart) => {
            const attributes:CartAttribute[] = [
                {
                    key     : "cartId",
                    label   : "Cart ID",
                    value   : cart.cartId,
                },
                {
                    key     : "date",
                    label   : "Checkout Date",
                    value   : parsePhpDate(cart.date_modified),
                },
                {
                    key     : "itemsCount",
                    label   : "Orders",
                    value   : cart.items.length,
                },
                {
                    key     : "printReceipt",
                    label   : "Receipt",
                    run     : async () => printService.print(Receipt({cart})),
                },
            ];

            if (userLevel === COMPANY_LEVEL) {
                attributes.push({
                    key     : "branch",
                    label   : "Office",
                    value   : cart.items[0]?.branch?.branchName ?? "?",
                });
            }
            if (userLevel !== AGENT_LEVEL) {
                attributes.push({
                    key     : "agent",
                    label   : "User",
                    value   : cart.items[0]?.agent?.agentName ?? "?",
                });
            }

            if (userLevelCanSeePrices({company, userLevel}) || cartShouldShowPrice(cart)) {
                attributes.push({
                    key     : "priceTotal",
                    label   : "Total",
                    value   : mapCartAsPriceTotal(cart),
                });
            }

            return Object.fromEntries(attributes.map(attribute => [attribute.key, attribute]));
        }
    }))
    .run()
);

export const cartWasPaidWithCreditCard = (cart:Cart) => (
    (cart?.appliedPayments?.creditCards?.length ?? 0) > 0
);
export const cartShouldShowPrice = cartWasPaidWithCreditCard;

const mapCartAsPriceTotal = (cart:Cart): number => (
    cascadeSum(cart.items, "[*].price")
);

